import createCache from '@emotion/cache'
import { CacheProvider } from '@emotion/react'
import ReactDOM from "react-dom"

// Touches que les sites hôtes détournent pour piloter leur scroll.
const SCROLL_KEYS = [' ', 'Spacebar', 'ArrowUp', 'ArrowDown', 'ArrowLeft', 'ArrowRight', 'PageUp', 'PageDown', 'Home', 'End']

// Certains thèmes installent des raccourcis clavier de scroll (ex. le plugin clavier
// de smooth-scrollbar) qui déterminent le champ en cours de saisie via
// `document.activeElement`. Vu de l'extérieur du shadow DOM, celui-ci vaut le custom
// element et non le champ réellement focus : le site conclut qu'aucun champ n'est
// actif, avale la touche (preventDefault) et scrolle la page. L'espace ne s'inscrit
// alors plus dans les champs texte et ne coche plus les boutons radio.
// Ces listeners écoutant en phase bubble, il suffit de retenir l'évènement au niveau
// du shadow root quand le focus est à l'intérieur du formulaire : le champ reçoit sa
// touche normalement, mais le site ne la voit jamais.
function stopScrollKeysFromLeaking(shadowContainer) {
  shadowContainer.addEventListener('keydown', (event) => {
    if (shadowContainer.activeElement && SCROLL_KEYS.includes(event.key)) {
      event.stopPropagation()
    }
  })
}

// Crée un composant web personnalisé pour chaque composant de formulaire
export default function defineWebComponent(tag, Component) {
  class WebComponent extends HTMLElement {
    connectedCallback() {
      // connectedCallback est rejoué à chaque fois que l'élément est déplacé dans le
      // DOM. Certains thèmes déplacent tout le contenu de la page après coup (ex.
      // smooth-scrollbar, qui range les enfants du conteneur dans un .scroll-content),
      // ce qui déclenche un second appel : sans ce garde, attachShadow lève un
      // NotSupportedError et casse le reste du script de la page.
      if (this.isMounted) return
      this.isMounted = true

      const shadowContainer = this.attachShadow({ mode: 'closed' })
      const shadowRootElement = document.createElement('div')
      shadowContainer.appendChild(shadowRootElement)
      stopScrollKeysFromLeaking(shadowContainer)

      const props = {}
      for (const attribute of this.attributes) {
        const { name, value } = attribute
        props[name] = value
      }

      const cache = createCache({
        key: 'css',
        prepend: true,
        container: shadowContainer,
        // En production Emotion injecte ses règles via CSSOM (sheet.insertRule) et
        // laisse les balises <style> vides. Or un déplacement de l'élément dans le
        // DOM détache puis rattache ces <style> : le navigateur reconstruit alors la
        // feuille à partir du textContent (vide) et toutes les règles sont perdues,
        // le formulaire s'affiche sans aucun style. speedy: false écrit les règles
        // dans le textContent, elles survivent au déplacement.
        speedy: false,
      })
      ReactDOM.render(<CacheProvider value={cache}><Component {...props} /></CacheProvider>, shadowRootElement)
    }
  }
  customElements.define(tag, WebComponent)
}
